import { Button, Checkbox, FileUpload } from '@hai/ui-react';
import { IIconNames, IIconProps } from '@hai/ui-react/dist/icons/Icon';
import { Accept, ButtonStateType, FileUploadStages, IUploadState } from '@hai/ui-react/dist/types';
import { isEmpty, isNil } from 'ramda';
import React, { FormEvent, useEffect, useState } from 'react';
import { t } from 'src/js/i18n';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';

interface Props {
  accept?: Accept;
  browseMessage?: string;
  cancelBtnDisabledNoFile?: boolean;
  checkedCheckbox?: boolean;
  conjunction?: string;
  errorMessage?: string;
  file?: File | File[];
  hint?: string;
  icon?: IIconNames | IIconProps | React.ReactElement;
  mainMessage?: string;
  mainMessageAfterSelection?: string;
  /** multiple files allowed */
  maxSize?: number;
  /* Only works for single files upload
    TODO implement for each file of multiples */
  maxSizeText?: string;
  maxSizeCallback?: (tooLarge: boolean) => void;
  multiple?: boolean;
  onCancel?: VoidFunction;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** onFileChange optional. Return false or 0 to signal that it's handled and
   *  that the caller is handling file selection memory, also thus setting `file` prop.
   *  Otherwise selected file(s) will be tracked internally */
  onFileChange?: (files: File[]) => boolean | number;
  /** If you pass onFileChange, you need to pass dynamic `file` as well
   * If multiple == true use handler with File[]:
   * onUpload = (files: File[]) => ...
   * If multiple == false use handler with File:
   * onUpload = (files: File) => ...
   * */
  onUpload?: (file: any | File | File[], event?: FormEvent<HTMLFormElement>) => Promise<any> | void;
  onUploadError?: (newuploadState: IUploadState) => void;
  showCheckboxes?: boolean;
  successMessage?: string;
  uploadBtnText?: string;
  uploadBtnDisabled?: boolean;
  uploadCheckboxText?: string;
  uploadErrorMessage?: string;
  uploadState?: IUploadState;
  uploadBtnState?: ButtonStateType;
  onKeyUp?: any;
}

export const UploadZone: React.FunctionComponent<Props> = ({
  accept,
  browseMessage = t('upload.browseMessage'),
  cancelBtnDisabledNoFile = false,
  checkedCheckbox = false,
  conjunction = t('upload.or'),
  errorMessage = t('upload.incorrectFileType'),
  file,
  hint,
  icon,
  mainMessage = t('upload.mainMessage'),
  mainMessageAfterSelection = t('upload.mainMessage'),
  maxSize,
  maxSizeText,
  maxSizeCallback,
  multiple = false,
  onCancel,
  onCheckboxChange,
  onFileChange,
  onUpload,
  onUploadError,
  showCheckboxes = false,
  successMessage = t('upload.uploadSuccessMessage'),
  uploadBtnText = t('general.upload'),
  uploadBtnDisabled = false,
  uploadCheckboxText,
  uploadErrorMessage = t('upload.uploadErrorMessage'),
  uploadState,
  uploadBtnState,
  onKeyUp,
}) => {
  const [localFile, setLocalFile] = useState<File | File[]>(file);

  const localUploadState: IUploadState = {
    stage: FileUploadStages.NONE,
    progress: 0,
  };
  // TODO remove uneeded useEffect
  useEffect(() => {
    setLocalFile(file);
  }, [file]);

  const handleLocalFileChange = (files: File[]) => {
    if (onFileChange) {
      const retVal = onFileChange(files);
      if (retVal == 0) {
        return; // handled
      }
    }
    if (multiple) {
      setLocalFile(isEmpty(files) ? undefined : files);
      //TODO: implement maxFile check for each file of multiples
    } else {
      setLocalFile(isEmpty(files) ? undefined : files[0]);
      if (maxSize > 0 && files && files.length && files[0].size > maxSize) {
        createAndDispatchNotification(maxSizeText, NotificationVariant.ERROR, t);
        maxSizeCallback?.(true);
      } else {
        maxSizeCallback?.(false);
      }
    }
  };

  const handleCancel = () => {
    setLocalFile(undefined);
    onCancel?.();
  };

  return (
    <>
      <FileUpload
        accept={accept}
        //  onKeyUp={onKeyUp}
        {...{ onKeyUp: onKeyUp }}
        dropzoneMessage={{
          browseMessage: browseMessage,
          mainMessage: localFile ? mainMessageAfterSelection : mainMessage,
          conjunction: conjunction,
        }}
        errorMessage={errorMessage}
        files={
          onFileChange && file != null
            ? Array.isArray(file)
              ? file
              : [file]
            : localFile
            ? Array.isArray(localFile)
              ? localFile
              : [localFile]
            : []
        }
        hint={hint}
        icon={icon}
        multiple={multiple}
        onFileChange={handleLocalFileChange}
        successMessage={successMessage}
        uploadState={{
          ...(uploadState || localUploadState),
          resetMessage: t('upload.tryAgain'),
          uploadErrorMessage: uploadErrorMessage,
        }}
        onUploadError={onUploadError}
      />
      {(onUpload || showCheckboxes) && (
        <div className="d-flex hai-mt-6">
          {showCheckboxes ? (
            <div>
              <Checkbox checked={checkedCheckbox} onChange={onCheckboxChange}>
                {uploadCheckboxText}
              </Checkbox>
            </div>
          ) : (
            ''
          )}
          {onUpload && (
            <div className="ml-auto">
              <Button
                className="hai-mr-6"
                variant="secondary"
                onClick={handleCancel}
                disabled={cancelBtnDisabledNoFile ? isNil(localFile) : false}
              >
                {t('general.cancel')}
              </Button>
              <Button
                //  id="id-button-upload"
                {...{ id: 'id-button-upload' }}
                variant="primary"
                onClick={(e: FormEvent<HTMLFormElement>) => {
                  onUpload(localFile, e);
                }}
                disabled={uploadBtnDisabled || isNil(localFile)}
                state={uploadBtnState}
                //    onKeyUp={onKeyUp}
                {...{ onKeyUp: onKeyUp }}
              >
                {uploadBtnText}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
